// app/components/Sidebar.tsx
import { View, ScrollView, Platform, Linking, type ViewStyle } from 'react-native';
import { Button, Drawer, Text, Surface, Portal, Modal, useTheme, Badge } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter, usePathname } from 'expo-router';
import Logo from './Logo';
import { FleetSwitcher } from './FleetSwitcher';
import { useCompanyContext } from '../context/CompanyContext';
// Removed useUser to avoid duplicate network calls on component mount during resizes
import { useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createSelfPayBillingPortalSession } from '../api/apiService';
import { API_BASE, APP_ORIGIN } from '../api/config';
import { usePaywallStatusContext } from '../hooks/usePaywallStatus';
import { DriverSidebarHeader } from './DriverSidebarHeader';
import { AppStoreLinks } from './appStores/AppStoreLinks';
import { currentMobileWebPlatform } from '../utils/mobileWebPlatform';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  screenWidth: number;
  onLogout: () => void;
  isOwner?: boolean;
  isDriver?: boolean;
  selectedProfile?: 'Owner' | 'Driver';
  onSwitchProfile?: (newProfile: 'Owner' | 'Driver') => void;
  onRegisterDriver?: () => void;
  onCompleteDriverProfile?: () => void;
  /**
   * D-046 (2026-10-11): hide the become-driver entry when the company has no
   * free seat (or capacity is unknown/loading/errored), so the sidebar cannot
   * open the profile form without an available seat. Defaults to true for
   * callers that do not track capacity.
   */
  showBecomeDriver?: boolean;
  pendingTransfersCount?: number;
}

export const SIDEBAR_WIDTH = 320;

export const Sidebar = ({
  isOpen,
  onClose,
  screenWidth,
  onLogout,
  isOwner = false,
  isDriver = false,
  selectedProfile = 'Owner',
  onSwitchProfile,
  onRegisterDriver,
  onCompleteDriverProfile,
  showBecomeDriver = true,
  pendingTransfersCount = 0
}: SidebarProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const isLargeScreen = screenWidth > 1024;
  const { companies, selectedCompany, setSelectedCompany } = useCompanyContext();
  const paywall = usePaywallStatusContext();
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState<string | null>(null);
  const [showInstallLinks, setShowInstallLinks] = useState(false);
  
  // Determine effective profile based on user roles and current selection.
  // Menu rendering and FleetSwitcher visibility follow the active profile, not the
  // raw capability — a dual-role user viewing as Driver must see the Driver menu
  // even though isOwner is true.
  const effectiveProfile: 'Owner' | 'Driver' = (() => {
    if (isDriver && !isOwner) return 'Driver';
    if (isOwner && !isDriver) return 'Owner';
    return selectedProfile;
  })();

  const shouldShowFleetSwitcher = effectiveProfile === 'Owner';
  const canShowInstallLinks = isDriver && effectiveProfile === 'Driver' &&
    Platform.OS === 'web' && currentMobileWebPlatform() !== null;
  useEffect(() => {
    if (!canShowInstallLinks || !isOpen) setShowInstallLinks(false);
  }, [canShowInstallLinks, isOpen]);

  const hasOwner = isOwner;
  const hasDriver = isDriver;
  // D-049: personal subscription management stays reachable in trial and payment trouble, not only when active.
  const canManageSelfPaySubscription =
    effectiveProfile === 'Driver' &&
    paywall?.status?.is_self_pay === true &&
    (paywall.status.state === 'active' || paywall.status.state === 'trialing' ||
      paywall.status.payment_action_required === true);

  const handleManageSelfPaySubscription = async () => {
    if (isOpeningBillingPortal || !canManageSelfPaySubscription) return;
    setIsOpeningBillingPortal(true);
    setBillingPortalError(null);
    try {
      const frontendOrigin =
        Platform.OS === 'web' && typeof window !== 'undefined'
          ? window.location.origin
          : APP_ORIGIN;
      const returnUrl = `${frontendOrigin}/dashboard/home`;
      const response = await createSelfPayBillingPortalSession(
        returnUrl,
        `${API_BASE}/api/subscriptions/billing_portal/`,
        Platform.OS !== 'web',
      );
      if (!response.ok || !response.data?.url) {
        throw new Error(response.errorMessage || 'Unable to open billing portal');
      }
      await Linking.openURL(response.data.url);
      onClose();
    } catch (error) {
      setBillingPortalError(error instanceof Error ? error.message : 'Unable to open billing portal');
    } finally {
      setIsOpeningBillingPortal(false);
    }
  };

  const handleSwitchProfile = async () => {
    const newProfile = effectiveProfile === 'Owner' ? 'Driver' : 'Owner';

    // Store the selected profile in local storage for persistence
    try {
      await AsyncStorage.setItem('selectedProfile', newProfile);
    } catch (error) {
      console.error('Error saving profile to storage:', error);
    }

    // Always redirect to home page for profile switching
    // This ensures consistent behavior across all pages
    router.replace('/dashboard/home');

    // Call the onSwitchProfile callback if provided (for any additional logic)
    if (onSwitchProfile) {
      onSwitchProfile(newProfile);
    }
  };

  const renderContent = () => (
    <Drawer.Section style={{ paddingTop: 16 }}>
      {isLargeScreen && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 24, alignItems: 'center' }}>
          <Logo
            size={Platform.OS === 'web' ? 'medium' : 'mobile'}
            style={{ maxWidth: Platform.OS === 'web' ? 220 : 250 }}
          />
        </View>
      )}

      {effectiveProfile === 'Driver' && (isLargeScreen || isOpen) && (
        <DriverSidebarHeader refreshKey={pathname || ''} />
      )}

      {/* Fleet Switcher for multi-company owners or users with companies */}
      {shouldShowFleetSwitcher && (
        <FleetSwitcher
          isOwner={isOwner}
          onCompanyChange={() => router.replace('/dashboard/home')}
          companiesOverride={companies}
          selectedCompanyOverride={selectedCompany}
          setSelectedCompanyOverride={setSelectedCompany}
        />
      )}

      <Drawer.Item
        label={t('home')}
        icon="home"
        active={pathname === '/dashboard/home'}
        onPress={() => {
          router.push('/dashboard/home');
          onClose();
        }}
      />
      {effectiveProfile === 'Owner' ? (
        <>
          <Drawer.Item
            label={t('drivers.menu', 'Drivers')}
            icon="account-group"
            active={pathname?.startsWith('/dashboard/drivers')}
            onPress={() => {
              router.push('/dashboard/drivers');
              onClose();
            }}
            right={props =>
              pendingTransfersCount > 0 ? (
                <Badge
                  size={20}
                  style={{
                    backgroundColor: theme.colors.error,
                    color: theme.colors.onError
                  }}
                >
                  {pendingTransfersCount}
                </Badge>
              ) : null
            }
          />
          <Drawer.Item
            label={t('vehicles.menu', 'Vehicles')}
            icon="car"
            active={pathname?.startsWith('/dashboard/vehicles')}
            onPress={() => {
              router.push('/dashboard/vehicles');
              onClose();
            }}
          />
          <Drawer.Item
            label={t('manageLicenses')}
            icon="license"
            active={pathname === '/dashboard/manage-licenses'}
            testID="owner-sidebar-manage-licenses"
            onPress={() => {
              router.push('/dashboard/manage-licenses');
              onClose();
            }}
          />
          <Drawer.Item
            label={t('gps.title', 'GPS Tracking')}
            icon="map-marker-path"
            active={pathname?.startsWith('/dashboard/gps')}
            onPress={() => {
              router.push('/dashboard/gps');
              onClose();
            }}
          />
          <Drawer.Item
            label={t('companyInfo.menu', 'Company')}
            icon="office-building"
            active={pathname === '/dashboard/company'}
            onPress={() => {
              router.push('/dashboard/company');
              onClose();
            }}
          />
          <Drawer.Item
            accessibilityLabel={t('userData.title')}
            label={t('userData.title')}
            icon="account-details"
            active={pathname === '/dashboard/user-data'}
            onPress={() => {
              router.push('/dashboard/user-data');
              onClose();
            }}
          />
          {/* Show drivers location option only on small screens */}
          {!isLargeScreen && (
            <Drawer.Item
              label={t('ownerDashboard.driverLocations', 'Driver Locations')}
              icon="map-marker-multiple"
              active={pathname === '/dashboard/drivers-location'}
              onPress={() => {
                router.push('/dashboard/drivers-location');
                onClose();
              }}
            />
          )}
        </>
      ) : (
        <>
          {/* Removed explicit Daily Report entry; unified under Reports with Daily as default */}
          <Drawer.Item
            label={t('reports')}
            icon="file-document"
            onPress={() => {
              router.push('/dashboard/report');
              onClose();
            }}
          />
          <Drawer.Item
            label={t('myVehicles.menu', 'My Vehicles')}
            icon="car-multiple"
            active={pathname === '/dashboard/my-vehicles'}
            onPress={() => {
              router.push('/dashboard/my-vehicles');
              onClose();
            }}
          />
          <Drawer.Item
            label={t('settings')}
            icon="cog"
            onPress={() => {
              router.push('/dashboard/settings');
              onClose();
            }}
          />
          {canManageSelfPaySubscription && (
            <Drawer.Item
              label={t('manageSubscription')}
              icon="credit-card-settings"
              active={false}
              testID="driver-sidebar-manage-self-pay"
              disabled={isOpeningBillingPortal}
              onPress={() => {
                void handleManageSelfPaySubscription();
              }}
            />
          )}
          <Drawer.Item
            label={t('userData.title')}
            icon="account-details"
            onPress={() => {
              router.push('/dashboard/user-data');
              onClose();
            }}
          />
        </>
      )}
      {canShowInstallLinks && (
        <Drawer.Item testID="driver-sidebar-download-app" icon="cellphone-arrow-down"
          label={t('register:downloadApp')} onPress={() => setShowInstallLinks(true)} />
      )}
      {/* Profile action: switch or register */}
      {hasOwner && hasDriver && (
        <Drawer.Item
          testID="switch-profile-item"
          label={effectiveProfile === 'Owner' ? t('switchToDriver', 'Switch to Driver') : t('switchToOwner', 'Switch to Owner')}
          icon="swap-horizontal"
          onPress={handleSwitchProfile}
        />
      )}
      {hasOwner && !hasDriver && showBecomeDriver && (
        <Drawer.Item
          label={t('ownerDashboard.becomeDriverCta', 'Tornar-me motorista')}
          icon="steering"
          onPress={() => {
            if (onCompleteDriverProfile) {
              onCompleteDriverProfile();
            } else {
              router.push('/account/complete-driver');
            }
            onClose();
          }}
        />
      )}
      <Drawer.Item
        label={t('logout')}
        icon="logout"
        onPress={() => {
          onLogout();
          onClose();
        }}
      />
    </Drawer.Section>
  );

  return (
    <>
      {screenWidth > 1024 ? (
        <Surface style={{
          width: SIDEBAR_WIDTH,
          height: '100%',
          position: Platform.OS === 'web' ? ('fixed' as ViewStyle['position']) : 'absolute',
          left: 0,
          top: 0,
          zIndex: 2
        }}>
          <ScrollView style={{ flex: 1 }}>{renderContent()}</ScrollView>
        </Surface>
      ) : (
        <Portal>
          <Modal
            visible={isOpen}
            onDismiss={onClose}
            contentContainerStyle={[
              { backgroundColor: theme.colors.surface, width: SIDEBAR_WIDTH, height: '100%' },
              Platform.OS === 'web' ? {
                maxWidth: SIDEBAR_WIDTH,
                width: SIDEBAR_WIDTH,
                alignSelf: 'flex-start',
              } : {}
            ]}
          >
            <ScrollView style={{ position: 'absolute', top: 66, bottom: 0, width: SIDEBAR_WIDTH }} contentContainerStyle={{ paddingTop: 16, paddingBottom: 16 }}>{renderContent()}</ScrollView>
            <Button mode="text" icon="close" onPress={onClose} style={{ position: 'absolute', top: 16, right: 16 }}>
              {t('close')}
            </Button>
          </Modal>
        </Portal>
      )}
      {canShowInstallLinks && showInstallLinks && <Portal>
        <Modal visible onDismiss={() => setShowInstallLinks(false)}
          contentContainerStyle={{ margin: 16, padding: 16, backgroundColor: theme.colors.surface, borderRadius: 16 }}>
          <ScrollView>
            <AppStoreLinks testID="driver-install-store-links" />
            <Button onPress={() => setShowInstallLinks(false)}>{t('close')}</Button>
          </ScrollView>
        </Modal>
      </Portal>}
      <Portal>
        <Modal
          visible={billingPortalError !== null}
          onDismiss={() => setBillingPortalError(null)}
          contentContainerStyle={{
            backgroundColor: theme.colors.surface,
            margin: 24,
            padding: 24,
            borderRadius: 8,
          }}
        >
          <Text>{billingPortalError}</Text>
          <Button mode="contained" onPress={() => setBillingPortalError(null)}>
            {t('close')}
          </Button>
        </Modal>
      </Portal>
    </>
  );
};





